import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, Download, Upload, Users, GraduationCap, Shield, BarChart3 } from 'lucide-react';
import { useUsers, useUserStats } from './hooks';
import { CreateUserModal, UserList, ImportUserModal } from './components';
import toast from 'react-hot-toast';
import type { User, UserSubmitRequest } from './types';

const UserDashboard: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const queryClient = useQueryClient();

  const {
    users,
    pagination,
    filters,
    page,
    perPage,
    isLoading,
    isError,
    updateFilters,
    updatePagination,
    resetFilters,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    isCreating,
    isUpdating,
    isDeleting,
    createUserMutation,
    deleteUserMutation,
    toggleStatusMutation,
    refetch
  } = useUsers();

  const handleAddUser = () => {
    setEditingUser(null);
    setShowCreateModal(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowCreateModal(true);
  };

  const handleSubmitUser = (userData: UserSubmitRequest) => {
    if (editingUser) {
      const userId = editingUser.id || editingUser.user_name || editingUser.code || '';
      if (userId) {
        updateUser({ id: userId, data: userData });
      }
    } else {
      createUser(userData, {
        onSuccess: (response: any) => {
          // Show password if it was generated or provided
          if (response?.plain_password) {
            toast.success(
              <div className="flex flex-col space-y-2">
                <div className="font-medium">Tạo người dùng thành công!</div>
                <div className="text-sm">
                  <strong>Mật khẩu:</strong> {response.plain_password}
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(response.plain_password)}
                  className="text-xs bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded transition-colors"
                >
                  Copy mật khẩu
                </button>
              </div>,
              { duration: 10000 }
            );
          } else {
            toast.success('Tạo người dùng thành công!');
          }
        },
        onError: (error) => {
          toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi tạo người dùng');
        }
      });
    }
    setShowCreateModal(false);
    setEditingUser(null);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingUser(null);
  };

  const handleDeleteUser = (userId: string, userName: string) => {
    toast((t) => (
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <span className="text-lg">🗑️</span>
          <span className="font-medium">Xác nhận xóa người dùng</span>
        </div>
        <p className="text-sm text-gray-600">
          Bạn có chắc chắn muốn xóa người dùng <strong>"{userName}"</strong>?
          <br />
          <span className="text-red-600">Hành động này không thể hoàn tác!</span>
        </p>
        <div className="flex space-x-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              toast.promise(
                deleteUserMutation.mutateAsync(userId),
                {
                  loading: 'Đang xóa người dùng...',
                  success: `Xóa người dùng "${userName}" thành công!`,
                  error: (err) => err?.response?.data?.message || 'Có lỗi xảy ra khi xóa người dùng',
                }
              );
            }}
            className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
          >
            Xóa
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: '#fff',
        color: '#000',
        maxWidth: '400px',
      },
    });
  };

  const handleToggleStatus = (userId: string, userName: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const actionText = currentStatus === 'active' ? 'tạm dừng' : 'kích hoạt';
    toast((t) => (
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{currentStatus === 'active' ? '⏸️' : '▶️'}</span>
          <span className="font-medium">Xác nhận {actionText} người dùng</span>
        </div>
        <p className="text-sm text-gray-600">
          Bạn có chắc chắn muốn {actionText} người dùng <strong>"{userName}"</strong>?
        </p>
        <div className="flex space-x-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              toast.promise(
                toggleStatusMutation.mutateAsync({ id: userId, status: newStatus }),
                {
                  loading: `Đang ${actionText} người dùng...`,
                  success: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} người dùng "${userName}" thành công!`,
                  error: (err) => err?.response?.data?.message || `Có lỗi xảy ra khi ${actionText} người dùng`,
                }
              );
            }}
            className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
          >
            {actionText.charAt(0).toUpperCase() + actionText.slice(1)}
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: '#fff',
        color: '#000',
        maxWidth: '400px',
      },
    });
  };

  const handleViewUser = (user: User) => {
    // TODO: Implement user detail view
    toast.success(`Xem chi tiết người dùng: ${user.name}`);
  };

  const handleResetPassword = (userId: string, userName: string) => {
    toast((t) => (
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <span className="text-lg">🔑</span>
          <span className="font-medium">Xác nhận đặt lại mật khẩu</span>
        </div>
        <p className="text-sm text-gray-600">
          Bạn có chắc chắn muốn đặt lại mật khẩu cho người dùng <strong>"{userName}"</strong>?
          <br />
          <span className="text-orange-600">Mật khẩu mới gồm 6 ký tự ngẫu nhiên (chữ và số) sẽ được hiển thị.</span>
        </p>
        <div className="flex space-x-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                const loadingToast = toast.loading('Đang đặt lại mật khẩu...');
                const userService = (await import('./services/userService')).default;
                const result = await userService.resetPassword(userId);
                toast.dismiss(loadingToast);
                
                toast.success(
                  <div className="flex flex-col space-y-2">
                    <div className="font-medium">Đặt lại mật khẩu thành công!</div>
                    <div className="text-sm">
                      <strong>Người dùng:</strong> {userName}
                    </div>
                    <div className="text-sm">
                      <strong>Mật khẩu mới:</strong> {result.plain_password}
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(result.plain_password)}
                      className="text-xs bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded transition-colors"
                    >
                      Copy mật khẩu
                    </button>
                  </div>,
                  { duration: 15000 }
                );
                refetch();
              } catch (error: any) {
                toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi đặt lại mật khẩu');
              }
            }}
            className="px-3 py-1 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded transition-colors"
          >
            Đặt lại mật khẩu
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: '#fff',
        color: '#000',
        maxWidth: '400px',
      },
    });
  };

  const handleFilterChange = (key: string, value: string) => {
    updateFilters({ [key]: value });
  };

  const handlePageChange = (page: number) => {
    updatePagination(page);
  };

  const handlePerPageChange = (perPage: number) => {
    updatePagination(1, perPage);
  };

  // Giữ modal mở để admin xem bảng user vừa import; modal tự đóng khi bấm "Hoàn thành"
  const handleImportSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  // Số toàn hệ thống từ API; mảng `users` chỉ là trang đang xem nên không đếm được.
  const { data: userStats } = useUserStats();
  const stats = {
    total: userStats?.total ?? 0,
    students: userStats?.students ?? 0,
    teachers: userStats?.teachers ?? 0,
    active: userStats?.active ?? 0
  };

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="w-full">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <div className="text-red-600 text-lg font-medium mb-2">
                Có lỗi xảy ra khi tải dữ liệu
              </div>
              <p className="text-gray-600 mb-4">
                Không thể kết nối đến server hoặc dữ liệu bị lỗi.
              </p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Thử lại
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Quản lý người dùng</h1>
              <p className="mt-2 text-gray-600">
                Quản lý tất cả người dùng trong hệ thống
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </button>
              <button
                onClick={handleAddUser}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Thêm người dùng
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Tổng số</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full">
                <GraduationCap className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Học sinh</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.students}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-full">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Giáo viên</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.teachers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full">
                <BarChart3 className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Đang hoạt động</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </div>
        </div>

        {/* User List */}
        <UserList
          users={users}
          isLoading={isLoading}
          filters={filters}
          onFilterChange={handleFilterChange}
          onEditUser={handleEditUser}
          onDeleteUser={handleDeleteUser}
          onToggleStatus={handleToggleStatus}
          onViewUser={handleViewUser}
          onResetPassword={handleResetPassword}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
        />

        {/* Modals */}
        <CreateUserModal
          isOpen={showCreateModal}
          onClose={handleCloseModal}
          onSubmit={handleSubmitUser}
          onResetPassword={handleResetPassword}
          isLoading={isCreating || isUpdating}
          editingUser={editingUser}
        />

        <ImportUserModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImportSuccess={handleImportSuccess}
        />
      </div>
    </div>
  );
};

export default UserDashboard;
